import React, { useEffect, useState } from 'react';
import HeroCreative from '../../../features/banners/HeroCreative';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

export const emptyBannerAppConfig = {
  enabled: true,
  appOnly: false,
  imageUrl: '',
  headline: '',
  headlineEn: '',
  subtitle: '',
  subtitleEn: '',
  showAd: true,
  showFeedDate: true,
  contentTone: 'auto',
  sourceIcon: 'none',
  fullCreative: true,
};

export default function BannerAppSettings({ value, onChange, file, onFileChange, fallbackImage }) {
  const [preview, setPreview] = useState('');
  const [error, setError] = useState('');
  const [previewLocale, setPreviewLocale] = useState('ko');
  useEffect(() => {
    if (!file) {
      setPreview('');
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  const change = (key, next) =>
    onChange({
      ...value,
      [key]: next,
      ...(value.feedPostId && ['headline', 'headlineEn', 'subtitle', 'subtitleEn'].includes(key)
        ? { feedCopyMode: 'manual' }
        : {}),
    });
  const image = preview || value.imageUrl || fallbackImage;
  return (
    <Stack spacing={2}>
      <Typography variant="h6">홈 상단 롤링 이미지 · 앱·웹 공통</Typography>
      <Alert severity="info">
        앱과 웹에 같은 이미지·문구·순서를 사용합니다. 이미지는 14:9, 권장 1400×900px이며, 아래
        이미지가 없으면 대표 이미지를 사용합니다. 화면 폭에 따라 동시에 보이는 개수만 달라집니다.
      </Alert>
      {error && <Alert severity="error">{error}</Alert>}
      <FormControlLabel
        label="앱·웹 홈에 공개"
        control={
          <Checkbox checked={value.enabled} onChange={(e) => change('enabled', e.target.checked)} />
        }
      />
      {Boolean(value.feedPostId) && (
        <Box>
          <FormControlLabel
            label="피드 날짜 표시"
            control={
              <Checkbox
                checked={value.showFeedDate !== false}
                onChange={(e) => change('showFeedDate', e.target.checked)}
              />
            }
          />
          <Typography variant="caption" display="block">
            끄면 앱 홈 롤링 이미지에서 게시 날짜를 숨깁니다. 피드 원문의 날짜는 유지됩니다.
          </Typography>
        </Box>
      )}
      <FormControlLabel
        label="AD 표시"
        control={
          <Checkbox checked={value.showAd} onChange={(e) => change('showAd', e.target.checked)} />
        }
      />
      <Button component="label" variant="outlined">
        배너 이미지 선택
        <input
          hidden
          aria-label="홈 배너 이미지 파일"
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={(e) => {
            const next = e.target.files?.[0];
            e.target.value = '';
            if (!next) return;
            if (
              !['image/png', 'image/jpeg', 'image/webp'].includes(next.type) ||
              next.size > 5 * 1024 * 1024
            ) {
              setError('PNG/JPG/WebP 5MB 이하 이미지를 선택해주세요.');
              return;
            }
            setError('');
            onFileChange(next);
          }}
        />
      </Button>
      <Button
        onClick={() => {
          onFileChange(null);
          change('imageUrl', '');
        }}
      >
        별도 이미지 해제 (대표 이미지 사용)
      </Button>
      {['headline', 'headlineEn', 'subtitle', 'subtitleEn'].map((key, i) => (
        <TextField
          key={key}
          label={
            [
              '배너 제목 (선택)',
              '배너 영문 제목 (선택)',
              '배너 설명 (선택)',
              '배너 영문 설명 (선택)',
            ][i]
          }
          value={value[key]}
          inputProps={{ maxLength: i < 2 ? 160 : 300 }}
          onChange={(e) => change(key, e.target.value)}
        />
      ))}
      <TextField
        select
        label="문구 색"
        value={value.contentTone}
        onChange={(e) => change('contentTone', e.target.value)}
      >
        <MenuItem value="auto">이미지에 따라 자동</MenuItem>
        <MenuItem value="light">흰색</MenuItem>
        <MenuItem value="dark">검정</MenuItem>
      </TextField>
      <TextField
        select
        label="제목 아이콘"
        value={value.sourceIcon}
        onChange={(e) => change('sourceIcon', e.target.value)}
      >
        <MenuItem value="none">없음</MenuItem>
        <MenuItem value="youtube">YouTube</MenuItem>
      </TextField>
      <TextField
        select
        label="미리보기 언어"
        value={previewLocale}
        onChange={(e) => setPreviewLocale(e.target.value)}
      >
        <MenuItem value="ko">한국어</MenuItem>
        <MenuItem value="en">English</MenuItem>
      </TextField>
      {image && (
        <Box sx={{ width: '100%', maxWidth: 353, borderRadius: '12px', overflow: 'hidden' }}>
          <HeroCreative
            banner={{ appConfig: { ...value, imageUrl: image } }}
            locale={previewLocale}
            imageAlt="홈 배너 미리보기"
          />
        </Box>
      )}
      <Typography variant="caption">
        미리보기는 웹과 같은 카드로 표시됩니다. 제목 1줄·설명 2줄이며, 앱의 자동 색상·네이티브
        블러는 기기에서 최종 확인하세요.
      </Typography>
    </Stack>
  );
}
